import { Component, OnInit, OnDestroy, Input, OnChanges, ViewChild, AfterContentInit } from '@angular/core';
import { trigger, state, transition, animate, style } from '@angular/animations';
import { Subscription } from 'rxjs';
import { SotiPopupTrigger } from '@soti/popup';
import { WorkPanelBarItem } from '../models/work-panel-bar-items';
import { RemoteControlDeviceState } from 'app/modules/remote-control/services/device-state-registry.service';
import { XsI18nPipe } from '@nx-fe/pipes';
import { GlobalEventBusService} from '@nx-fe/services';
import { GlobalEvents } from 'app/core/global-event-constants';
import { WorkPanelManagerService } from 'app/modules/remote-control/services/work-panel-manager.service';
import { WorkPanelType } from 'app/modules/remote-control/services/work-panel-manager.service';
import { WorkPanelRC2Component } from '../work-panel-rc2.component';
import { RemoteControlService } from 'app/modules/remote-control/services/remote-control.service';
import { DeviceStateRegistryService } from 'app/modules/remote-control/services/device-state-registry.service';

@Component({
  selector: 'app-work-panel-menu-bar',
  templateUrl: './work-panel-menu-bar.component.html',
  styleUrls: ['./work-panel-menu-bar.component.scss'],
  animations: [
    trigger('expandCollapseAnimation', [
      state('expanded', style({ height: '30px' })),
      state('collapsed', style({ height: '0px', display: 'none' })),
      transition('expanded => collapsed, collapsed => expanded', [animate('0.1s')]),
    ]),
  ],
})
export class WorkPanelMenuBarComponent implements OnInit, OnDestroy, OnChanges, AfterContentInit {
  @Input() menuBarItems: WorkPanelBarItem[];
  @Input() rcState: RemoteControlDeviceState;
  @Input() standAlone: boolean;
  @Input() collapsible: boolean;
  @Input() workPanelKey: string;

  @ViewChild('deviceLogsSelectorTrigger', { static: false }) deviceLogsSelectorTrigger: SotiPopupTrigger;
  @ViewChild('deviceLogsSelector', { static: false }) deviceLogsSelector: SotiPopupTrigger;

  public isMenuCollapsed = false;
  private _intersectionRatio = 0;
  private _menuIntersection$: IntersectionObserver;
  // If the menu is expanded manually (with the toggle button) we should not collapse it automatically when it goes out of screen, as it is expected
  private _toggledManually = false;
  private _changingTab = false;
  private _rcBarRestoredSub: Subscription;

  constructor(private saI18nPipe: XsI18nPipe, private globalEventBusService: GlobalEventBusService, private workPanelManagerService: WorkPanelManagerService, private rcService: RemoteControlService,  private devStrateRegService: DeviceStateRegistryService) {}

  ngOnInit() {
    if (this.collapsible) {
      this._rcBarRestoredSub = this.globalEventBusService.register(GlobalEvents.rcBarRestored, this.handleMenuCollapse);
      this.configureMenuIntersectionObserver();
    }
  }

  ngOnChanges() {
    if (this.collapsible) {
      this.handleMenuCollapse();
    }
  }

  ngOnDestroy(): void {
    if (this._menuIntersection$ !== undefined) {
      this._menuIntersection$.disconnect();
    }
    if (this._rcBarRestoredSub) {
      this._rcBarRestoredSub.unsubscribe();
    }
  }

  ngAfterContentInit(): void {
    // XTODO: The forced click was removed - it redirects from Snapshot open to RC unintentionally
    // setTimeout(()=>{
    //   this.click(this.menuBarItems[0]);
    // }, 4050);
  }

  private handleMenuCollapse = (): void => {
    // We check what's the last intersection of the menu, and act accordingly only
    // If the menu is partially visible, collapse it
    if (this._intersectionRatio > 0 && this._intersectionRatio < 1) {

        const menuBar = $('.rc-bars-container');
        const menuBarTotalHeight = menuBar.height() + menuBar.position().top;
        const menuBarContainer = $('.tab-views-wrapper');

        // Only collapse the menuy if it does not fit vertically
        if (menuBarTotalHeight > menuBarContainer.height()) {

            // Only collapse the menu if its visivility is partial, and it is not because
            // the user just expanded it (as we expect it to be partially visible in ths case)
            if (this._toggledManually) {
                this._toggledManually = false;
            } else {
                this.isMenuCollapsed = true;
            }
        }
    } else if (this._intersectionRatio === 0) {
        // If the menu is getting totally hidden, clear the flags
        this._toggledManually = false;
        this.isMenuCollapsed = false;
    }

    this._changingTab = false;
  };

  public isRemoteControlling = (): boolean => {
    const currentState = this.devStrateRegService.getCurrentState();
    return currentState && (currentState.active || currentState.connected);
  }

  private configureMenuIntersectionObserver = (): void => {
    // IntersectionObserver allows us to detect if an element intersect with another and on what ratio
    // We can use this to check if the menu is fully visible or partially hidden
    // threshold 0 and 1 means we will get an event when visivility goes through these two values on any direction
    this._menuIntersection$ = new IntersectionObserver(this.handleIntersectionChange, { threshold: [0, 1] });
    this._menuIntersection$.observe(document.querySelector('.rc-bars-container'));
  };

  private handleIntersectionChange = (entries: IntersectionObserverEntry[]): void => {
    if (entries && entries.length > 0) {
      // Update the menu visibility status...
      this._intersectionRatio = entries[0].intersectionRatio;

      if (
        (this.rcState && this.rcState.poppedOut) || // (Poped out)
        (this.rcState && !this.rcState.poppedOut && this.standAlone) || // (Stand Alone)
        this._changingTab
      ) {
        // Only call handleMenuCollapse if we are popped out as if we are NOT,
        // Menu collapse will be handle from "onRcBarRestored" event
        this.handleMenuCollapse();
      }
      this._changingTab = false;
    }
  };

  /**
   * Toggles the RC menu
   */
  public toggleMenu = (): boolean => {
    this.isMenuCollapsed = !this.isMenuCollapsed;
    this._toggledManually = true;

    return this.isMenuCollapsed;
  };

  public isSnapshotLive = (): boolean => {
    const workPanelType: WorkPanelType = this.workPanelManagerService.getPanelType(this.workPanelKey);

    if (workPanelType == WorkPanelType.liveSnapshot || workPanelType == WorkPanelType.savedSnapshot) {
      return true;
    }
    return false;
  }

  public getToggleButtonIcon = (): string => this.isMenuCollapsed ? 'hd-icon-sortdown' : 'hd-icon-sortup';

  public getToggleButtonTooltip = (): string => this.isMenuCollapsed
      ? this.saI18nPipe.transform('ticket_RemoteControlMenu_ToggleButtonTooltipExpand')
      : this.saI18nPipe.transform('ticket_RemoteControlMenu_ToggleButtonTooltipCollapse');

  public expandCollapseAnimationTrigger = (item: WorkPanelBarItem): string => !this.isMenuCollapsed || item.selected(item.tabIndex) ? 'expanded' : 'collapsed';

  public click = (item: WorkPanelBarItem): void => {
    if (item.isDisabled(item.tabIndex)) {
      return;
    }

    if (item.name === 'download device logs') {
      this.deviceLogsSelector.open();
    } else {
      item.onClick(item.tabIndex);
      this._toggledManually = false;

      // If the menu is not fully visible, and we wont be changing to a new tab, collapse it
      if (!item.tabIndex && this._intersectionRatio < 1 && this.collapsible) {
        this.toggleMenu();
      } else {
        this._changingTab = true;
      }
    }
  };
}
